import React from "react";
import styled from "styled-components";

import { ILesserOfChargesCalculation } from "../../types/claim";

const TotalAllowableSection = styled.div`
  padding: 20px 20px 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const TotalAllowableLabel = styled.div`
  font-size: 16px;
  color: #777;
  font-weight: 400;
`;

const TotalAllowableAmount = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: #000;
`;

const Block = styled.div`
  padding: 20px;
`;

const BlockTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px;
  color: #333;
`;

const BlockIntro = styled.p`
  font-size: 14px;
  color: #555;
  margin: 0 0 16px;
`;

const FormulaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormulaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 14px;
`;

const FormulaLabel = styled.div`
  color: #555;

  > span {
    color: #888;
    margin-left: 4px;
  }
`;

const FormulaAmount = styled.div`
  color: #333;
  min-width: 120px;
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  border-top: 2px solid #333;
  padding-top: 12px;
  margin-top: 12px;
  font-size: 15px;
  font-weight: 600;
`;

const RuleFooter = styled.div`
  margin-top: 16px;
  font-size: 13px;
  color: #777;
  font-style: italic;
`;

function formatCurrency(cents: number | null | undefined): string {
  if (cents == null) return "N/A";
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

function formatMultiplierPercent(multiplier: number): string {
  const pct = multiplier * 100;
  return `${Number.isInteger(pct) ? pct : pct.toFixed(2)}%`;
}

interface IPrimaryPricingBlockProps {
  calculation: ILesserOfChargesCalculation;
}

/**
 * Renders the lesser-of-charges block when it's the sole pricing source for the
 * claim (mode === "PRIMARY" — no fee schedule produced an expected, so the rule
 * IS the contract pricing rather than a cap on top of one). Visually distinct
 * from the CAP-mode card: there is no "Original Expected → New Expected" framing
 * because there is no original — only what this contract prices the claim at.
 */
export function PrimaryPricingBlock({
  calculation,
}: IPrimaryPricingBlockProps) {
  const {
    scope,
    multiplier,
    cappedExpectedInCents,
    totalChargeInCents,
    ruleName,
    perLineCaps,
  } = calculation;

  const multiplierPct = formatMultiplierPercent(multiplier);
  const multiplierDecimal = String(multiplier);
  const ruleLabel = ruleName ?? "Lesser of Charges";

  return (
    <>
      <TotalAllowableSection>
        <TotalAllowableLabel>Total Allowable:</TotalAllowableLabel>
        <TotalAllowableAmount>
          {formatCurrency(cappedExpectedInCents)}
        </TotalAllowableAmount>
      </TotalAllowableSection>

      <Block>
        <BlockTitle>Charge-based pricing (no fee schedule)</BlockTitle>

        {scope === "TOTAL_CHARGES" ? (
          <>
            <BlockIntro>
              This contract prices the claim as a percentage of billed charges.
            </BlockIntro>
            <FormulaList>
              <FormulaRow>
                <FormulaLabel>Multiplier</FormulaLabel>
                <FormulaAmount>{multiplierPct}</FormulaAmount>
              </FormulaRow>
              <FormulaRow>
                <FormulaLabel>Billed Charges</FormulaLabel>
                <FormulaAmount>
                  {formatCurrency(totalChargeInCents)}
                </FormulaAmount>
              </FormulaRow>
              <TotalRow>
                <div>
                  Expected ({multiplierDecimal} ×{" "}
                  {formatCurrency(totalChargeInCents)})
                </div>
                <FormulaAmount>
                  {formatCurrency(cappedExpectedInCents)}
                </FormulaAmount>
              </TotalRow>
            </FormulaList>
          </>
        ) : (
          <>
            <BlockIntro>
              This contract prices each line as a percentage of billed charges.
              Lines without a CPT are not priced and are omitted from the table.
            </BlockIntro>
            <FormulaList>
              <FormulaRow>
                <FormulaLabel>Multiplier</FormulaLabel>
                <FormulaAmount>{multiplierPct}</FormulaAmount>
              </FormulaRow>
              {(perLineCaps ?? []).map((line, idx) => {
                // Back-compute the line charge from the stored capped expected
                // so the row can read "{multiplier} × {charge} = {expected}".
                // The backend stores cappedExpectedInCents per line, not the
                // raw charge, so this is the only way to render the formula
                // without the backend sending an extra field.
                const lineChargeInCents =
                  multiplier > 0
                    ? Math.round(line.cappedExpectedInCents / multiplier)
                    : 0;
                return (
                  <FormulaRow
                    // eslint-disable-next-line react/no-array-index-key
                    key={`primary-${line.cptCode}-${idx}`}
                  >
                    <FormulaLabel>
                      {line.cptCode}
                      <span>
                        ({multiplierDecimal} ×{" "}
                        {formatCurrency(lineChargeInCents)})
                      </span>
                    </FormulaLabel>
                    <FormulaAmount>
                      {formatCurrency(line.cappedExpectedInCents)}
                    </FormulaAmount>
                  </FormulaRow>
                );
              })}
              <TotalRow>
                <div>Total Expected</div>
                <FormulaAmount>
                  {formatCurrency(cappedExpectedInCents)}
                </FormulaAmount>
              </TotalRow>
            </FormulaList>
          </>
        )}

        <RuleFooter>Applied rule: {ruleLabel}</RuleFooter>
      </Block>
    </>
  );
}
